import { useState, useCallback, useRef } from 'react';
import { useApiService } from '@/lib/api';

// Import conversation cache interface
interface ChatSummary {
  _id: string;
  title: string;
  isBeacon: boolean;
  isFavorite: boolean;
  isDeleted: boolean;
  createdAt: string;
  subjectId?: string;
  entityId?: string;
  history?: any[];
}

// Access global conversation cache (declared in use-conversation-cache.ts)
declare global {
  var globalConversationCache: {
    conversations: ChatSummary[];
    hasLoaded: boolean;
    currentPage: number;
    hasMore: boolean;
    lastFetchTime: number;
  } | undefined;
}

interface Beacon {
  id: string;
  name: string;
  description: string;
  type: string;
  subject_id: string;
  class_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface BeaconChat {
  _id: string;
  title: string;
  isBeacon: boolean;
  isFavorite: boolean;
  isDeleted: boolean;
  createdAt: string;
  subjectId?: string;
  entityId?: string;
  // Note: We don't cache history content - always fetch fresh from specific chat API
}

interface BeaconCacheEntry {
  beacon: Beacon;
  lastFetchTime: number;
}

// Global cache that persists across component re-mounts
const globalBeaconCache = new Map<string, BeaconCacheEntry>();

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

export const useBeaconCache = () => {
  const [isLoadingBeacon, setIsLoadingBeacon] = useState(false);
  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useApiService();
  const fetchPromiseRef = useRef<Map<string, Promise<any>>>(new Map());

  // Check if cache is still valid for a beacon
  const isCacheValid = useCallback((beaconId: string) => {
    const cached = globalBeaconCache.get(beaconId);
    if (!cached) return false;
    
    const now = Date.now();
    return (now - cached.lastFetchTime) < CACHE_DURATION;
  }, []);

  const fetchBeacon = useCallback(async (beaconId: string): Promise<Beacon> => {
    // Return cached data if valid
    if (isCacheValid(beaconId)) {
      const cached = globalBeaconCache.get(beaconId);
      return cached!.beacon;
    }

    // Prevent duplicate API calls for the same beacon
    const existingPromise = fetchPromiseRef.current.get(`beacon-${beaconId}`);
    if (existingPromise) {
      return await existingPromise;
    }

    try {
      setIsLoadingBeacon(true);
      setError(null);

      const fetchPromise = apiService.get<{
        success: boolean;
        data: Beacon;
      }>(`/entities/${beaconId}`);
      
      fetchPromiseRef.current.set(`beacon-${beaconId}`, fetchPromise);
      
      const response = await fetchPromise;

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch beacon data');
      }

      // Update cache
      globalBeaconCache.set(beaconId, {
        beacon: response.data,
        lastFetchTime: Date.now()
      });

      return response.data;
    } catch (error: any) {
      setError('Failed to load beacon data');
      throw error;
    } finally {
      setIsLoadingBeacon(false);
      fetchPromiseRef.current.delete(`beacon-${beaconId}`);
    }
  }, [apiService, isCacheValid]);

  const fetchBeaconChat = useCallback(async (beaconId: string): Promise<BeaconChat | null> => {
    // First check conversation cache for existing beacon chat metadata (not content)
    const conversationCache = (globalThis as any).globalConversationCache;
    if (conversationCache?.conversations) {
      const existingBeaconChat = conversationCache.conversations.find((chat: ChatSummary) => 
        chat.isBeacon && chat.entityId === beaconId && !chat.isDeleted
      );
      
      if (existingBeaconChat) {
        // Found existing beacon chat in conversation cache - return basic info without history
        const beaconChat: BeaconChat = {
          _id: existingBeaconChat._id,
          title: existingBeaconChat.title,
          isBeacon: true,
          isFavorite: existingBeaconChat.isFavorite,
          isDeleted: false,
          createdAt: existingBeaconChat.createdAt,
          subjectId: existingBeaconChat.subjectId,
          entityId: existingBeaconChat.entityId
        };

        return beaconChat;
      }
    }

    // Always fetch fresh chat data from API (no caching of chat content)
    try {
      setIsLoadingChat(true);
      setError(null);

      const chatData = await apiService.get<BeaconChat>(`/chats/${beaconId}/beacon`);
      return chatData;
    } catch (error: any) {
      // 404 is expected when no chat exists
      if (error.status === 404) {
        return null;
      }

      setError('Failed to load beacon chat');
      throw error;
    } finally {
      setIsLoadingChat(false);
    }
  }, [apiService]);

  const fetchBeaconData = useCallback(async (beaconId: string) => {
    // Check if we have beacon data cached
    const cached = globalBeaconCache.get(beaconId);
    const cacheValid = isCacheValid(beaconId);
    
    let beacon = cached?.beacon;
    
    // Fetch beacon data if not cached or invalid
    if (!cacheValid || !beacon) {
      try {
        beacon = await fetchBeacon(beaconId);
      } catch (error) {
        throw error; // Beacon data is required, so we throw
      }
    }
    
    // Always fetch chat data fresh (no caching of chat content)
    let chat: BeaconChat | null = null;
    try {
      chat = await fetchBeaconChat(beaconId);
    } catch (error) {
      // Chat fetch failure shouldn't prevent beacon chat page from loading
      chat = null; // Treat as no existing chat
    }
    
    return {
      beacon,
      chat
    };
  }, [fetchBeacon, fetchBeaconChat, isCacheValid]);

  const invalidateBeaconCache = useCallback((beaconId?: string) => {
    if (beaconId) {
      globalBeaconCache.delete(beaconId);
    } else {
      globalBeaconCache.clear();
    }
  }, []);

  const getCachedBeacon = useCallback((beaconId: string) => {
    return globalBeaconCache.get(beaconId);
  }, []);

  return {
    isLoadingBeacon,
    isLoadingChat,
    error,
    fetchBeaconData,
    fetchBeacon,
    fetchBeaconChat,
    invalidateBeaconCache,
    getCachedBeacon,
    isCacheValid
  };
};
